import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Download, Search, Tags } from "lucide-react";
import { API } from "../../../config";
import { formatEur } from "../../../pages/SmsCreditPage";

const TierBlock = ({ tier, open, onToggle }) => (
  <div className="rounded-xl border border-gray-800 bg-black/40" data-testid={`sms-tier-${tier.tier}`}>
    <button onClick={onToggle} className="w-full flex items-center gap-4 px-4 py-3 text-left" data-testid={`sms-tier-toggle-${tier.tier}`}>
      <div className="w-1 self-stretch rounded bg-[#E31837]" />
      <div className="flex-1">
        <p className="text-white font-semibold">{tier.label} <span className="text-xs text-gray-500 font-normal">· {tier.countries.length} {tier.countries.length === 1 ? "paese" : "paesi"}</span></p>
        <p className="text-xs text-gray-400">{tier.description}</p>
      </div>
      <p className="text-xl font-bold text-white" data-testid={`sms-tier-price-${tier.tier}`}>{formatEur(tier.price_eur, 3)}</p>
      <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1 px-5 pb-4 text-sm" data-testid={`sms-tier-countries-${tier.tier}`}>
        {tier.countries.map((c) => (
          <li key={c.prefix} className={c.operating ? "text-[#ff4d63] font-semibold" : "text-gray-300"}>
            {c.country} <span className="text-gray-500 font-normal">+{c.prefix}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const SmsPriceList = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  useEffect(() => { axios.get(`${API}/admin/sms-credit/price-tiers`).then((r) => setData(r.data)).catch(() => setData(null)); }, []);

  const tiers = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase().replace(/^\+/, "");
    if (!term) return data.tiers;
    return data.tiers
      .map((t) => ({ ...t, countries: t.countries.filter((c) => c.country.toLowerCase().includes(term) || c.prefix.startsWith(term)) }))
      .filter((t) => t.countries.length);
  }, [data, q]);

  const download = async () => {
    try {
      const r = await axios.get(`${API}/admin/sms-credit/price-list.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Listino_SMS_LoJack_v${data.version}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download listino non riuscito"); }
  };

  if (!data) return null;
  const searching = q.trim().length > 0;

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="sms-price-list">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="flex items-center gap-2 text-white font-semibold"><Tags size={18} /> Listino SMS riservato a LoJack</p>
          <p className="text-xs text-gray-400 mt-1" data-testid="sms-price-list-version">
            Versione {data.version} · valido dal {new Date(data.valid_from).toLocaleDateString("it-IT")} · prezzi per SMS scalati dal credito · in <span className="text-[#ff4d63]">rosso</span> i paesi già operativi
          </p>
        </div>
        <button onClick={download} className="btn-lojack-outline text-sm flex items-center gap-1" data-testid="sms-price-list-pdf">
          <Download size={14} /> Scarica listino PDF
        </button>
      </div>
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca paese o prefisso (es. Polonia o 48)"
          className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl pl-9 pr-3 py-2 text-white text-sm outline-none focus:border-gray-500" data-testid="sms-price-search" />
      </div>
      <div className="space-y-3">
        {tiers.length === 0 && <p className="text-sm text-gray-500" data-testid="sms-price-no-results">Nessun paese trovato</p>}
        {tiers.map((t) => (
          <TierBlock key={t.tier} tier={t} open={searching || !!open[t.tier]} onToggle={() => setOpen((o) => ({ ...o, [t.tier]: !o[t.tier] }))} />
        ))}
      </div>
      <p className="text-[11px] text-gray-500 mt-4">Un SMS contiene fino a 160 caratteri (70 con caratteri speciali di alcune lingue, es. polacco o greco): i messaggi più lunghi contano come più SMS.</p>
    </div>
  );
};
